import React from "react";
import { isAtRisk } from "./jobMockData";

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius:  { sm: 6, md: 8, lg: 12 },
  colors: {
    primary:   "#0F172A",
    secondary: "#334155",
    muted:     "#64748B",
    subtle:    "#94A3B8",
    border:    "#E2E8F0",
    bg:        "#F8FAFC",
    white:     "#FFFFFF"
  }
};

const statusColors = {
  requested: "#6366F1",
  assigned: "#0369A1",
  in_progress: "#D97706",
  completed: "#059669",
  cancelled: "#64748B",
  disputed: "#DC2626"
};

const JobAnalyticsPanel = ({ jobs }) => {
  const total = jobs.length;
  const totalRevenue = jobs.reduce((sum, j) => sum + j.price, 0);
  const avgJobValue = total === 0 ? 0 : totalRevenue / total;
  const highestJob = Math.max(...jobs.map(j => j.price), 0);
  
  const statusCounts = {
    requested: jobs.filter(j => j.status === "requested").length,
    assigned: jobs.filter(j => j.status === "assigned").length,
    in_progress: jobs.filter(j => j.status === "in_progress").length,
    completed: jobs.filter(j => j.status === "completed").length,
    cancelled: jobs.filter(j => j.status === "cancelled").length,
    disputed: jobs.filter(j => j.status === "disputed").length
  };
  
  const categoryCounts = {};
  jobs.forEach(job => {
    categoryCounts[job.category] = (categoryCounts[job.category] || 0) + 1;
  });
  const topCategories = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  const maxCategoryCount = topCategories[0]?.[1] || 1;
  
  const atRiskJobs = jobs.filter(j => isAtRisk(j));
  const disputedCount = jobs.filter(j => j.status === "disputed").length;
  const unpaidCompleted = jobs.filter(j => j.status === "completed" && j.paymentStatus === "pending").length;
  const cancelledLate = jobs.filter(j => j.status === "cancelled" && j.timeline && j.timeline.length > 1).length;

  return (
    <div style={{
      background: UI.colors.white,
      border: `1px solid ${UI.colors.border}`,
      borderRadius: UI.radius.lg,
      padding: UI.spacing.lg,
      marginBottom: UI.spacing.xl
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: UI.spacing.md
      }}>
        {/* Revenue Summary */}
        <div>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 13,
            fontWeight: 700,
            color: UI.colors.muted,
            marginBottom: UI.spacing.md,
            textTransform: "uppercase",
            letterSpacing: "0.05em"
          }}>Revenue</div>
          <div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 20,
              fontWeight: 800,
              color: UI.colors.primary,
              marginBottom: UI.spacing.xs
            }}>
              KSh {totalRevenue.toLocaleString()}
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12,
              color: UI.colors.muted,
              marginBottom: UI.spacing.xs
            }}>
              Avg: KSh {Math.round(avgJobValue).toLocaleString()}
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12,
              color: UI.colors.muted
            }}>
              Highest: KSh {highestJob.toLocaleString()}
            </div>
          </div>
        </div>
        
        {/* Job Flow */}
        <div>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 13,
            fontWeight: 700,
            color: UI.colors.muted,
            marginBottom: UI.spacing.md,
            textTransform: "uppercase",
            letterSpacing: "0.05em"
          }}>Job Flow</div>
          <div>
            {Object.entries(statusCounts).map(([status, count]) => {
              const percentage = total === 0 ? 0 : (count / total) * 100;
              return (
                <div key={status} style={{ marginBottom: UI.spacing.sm }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 2
                  }}>
                    <span style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11,
                      color: UI.colors.secondary,
                      textTransform: "capitalize"
                    }}>{status.replace("_", " ")}</span>
                    <span style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11,
                      fontWeight: 600,
                      color: UI.colors.primary
                    }}>{count}</span>
                  </div>
                  <div style={{
                    height: 6,
                    background: UI.colors.bg,
                    borderRadius: 3,
                    overflow: "hidden"
                  }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: "100%",
                      background: statusColors[status],
                      borderRadius: 3
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Top Categories */}
        <div>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 13,
            fontWeight: 700,
            color: UI.colors.muted,
            marginBottom: UI.spacing.md,
            textTransform: "uppercase",
            letterSpacing: "0.05em"
          }}>Top Categories</div>
          <div>
            {topCategories.map(([category, count]) => (
              <div key={category} style={{ marginBottom: UI.spacing.sm }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 2
                }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 11,
                    color: UI.colors.secondary
                  }}>{category}</span>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 11,
                    fontWeight: 600,
                    color: UI.colors.primary
                  }}>{count}</span>
                </div>
                <div style={{
                  height: 4,
                  background: "#EEF2FF",
                  borderRadius: 2,
                  overflow: "hidden"
                }}>
                  <div style={{
                    width: `${(count / maxCategoryCount) * 100}%`,
                    height: "100%",
                    background: "#6366F1",
                    borderRadius: 2
                  }} />
                </div>
              </div>
            ))}
            {topCategories.length === 0 && (
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12,
                color: UI.colors.subtle,
                textAlign: "center",
                padding: UI.spacing.lg
              }}>No data</div>
            )}
          </div>
        </div>
        
        {/* Risk Summary */}
        <div>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 13,
            fontWeight: 700,
            color: UI.colors.muted,
            marginBottom: UI.spacing.md,
            textTransform: "uppercase",
            letterSpacing: "0.05em"
          }}>Risk Summary</div>
          <div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 20,
              fontWeight: 800,
              color: "#DC2626",
              marginBottom: UI.spacing.xs
            }}>
              {atRiskJobs.length}
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 11,
              color: UI.colors.muted,
              marginBottom: UI.spacing.md
            }}>
              {total === 0 ? "0%" : `${Math.round((atRiskJobs.length / total) * 100)}% of all jobs`}
            </div>
            {atRiskJobs.length > 0 ? (
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 11,
                color: UI.colors.secondary
              }}>
                <div>{disputedCount} Disputed</div>
                <div>{unpaidCompleted} Unpaid Completed</div>
                <div>{cancelledLate} Cancelled Late</div>
              </div>
            ) : (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: UI.spacing.sm,
                color: "#059669"
              }}>
                <span style={{ fontSize: 14 }}>✓</span>
                <span style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 12,
                  fontWeight: 600
                }}>All clear</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobAnalyticsPanel;